import React, { useState, useEffect, useMemo } from "react"
import { connect } from "react-redux"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { Crosshair, Loader2, Send } from "lucide-react"

import styles from "./SendQuotePopup.module.scss"
import ModalOverlay from "../../UI/ModalOverlay/ModalOverlay.tsx"
import CustomSelect from "../../../pages/ProjectDocumentation/components/CustomSelect.tsx"
import LocationWithJurisdiction from "../../UI/LocationWithJurisdiction/LocationWithJurisdiction.tsx"
import { publishQuoteFromAutofillDraft, resetSuccess, sendQuote } from "../../../store/actions/categoryActions.js"
import useGetTodayDate from "../../../hooks/useGetTodayDate.js"
import { useGoogleMaps } from "../../../hooks/useGoogleMaps.ts"
import { fireAnalytics } from "../../../utils/firebase.js"
import { JURISDICTIONS } from "../../../constants/jurisdictions.ts"
import { mapGoogleProvinceToJurisdiction, sanitizeFormattedAddressForDisplay } from "../../../utils/jurisdictionFromPlace.ts"
import { reverseGeocodeLatLng } from "../../../utils/reverseGeocodeLatLng.ts"

const variants = {
  visible: {
    scale: 1,
    opacity: 1,
  },
  hidden: {
    scale: 0.95,
    opacity: 0,
  },
}

interface QuoteData {
  name: string
  city: string
  receiveFrom: string
  receiveTo: string
  currency: string
  description: string
  jurisdiccion: string
  zone: string
  delivery: boolean
  latitude?: number
  longitude?: number
}

interface CategoryRowLite {
  id: number | string
  name: string
}

interface SendQuotePopupProps {
  onClose: () => void
  onSuccess: () => void
  sendQuote: (quoteData: any, materials: any[], navigate: ReturnType<typeof useNavigate>) => Promise<void>
  publishQuoteFromAutofillDraft: (
    draftId: string,
    quoteData: any,
    materials: any[],
    categoryRows: CategoryRowLite[],
    navigate: ReturnType<typeof useNavigate>
  ) => Promise<void>
  materials: any[]
  setShowPreview: (value: boolean) => void
  autofillDraftId?: string | null
  categoryRowsForAutofill?: CategoryRowLite[]
}

const getSuggestedQuoteName = (todayDate: string) => {
  if (!todayDate) return ""
  return `Cotización ${todayDate}`
}

const SendQuotePopup: React.FC<SendQuotePopupProps> = ({
  onClose,
  sendQuote,
  publishQuoteFromAutofillDraft: publishFromAutofill,
  materials,
  onSuccess,
  setShowPreview,
  autofillDraftId = null,
  categoryRowsForAutofill = [],
}) => {
  const todayDate = useGetTodayDate()
  const suggestedName = useMemo(() => getSuggestedQuoteName(todayDate), [todayDate])
  const [hasInitializedName, setHasInitializedName] = useState(false)

  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [geolocationMessage, setGeolocationMessage] = useState<string | null>(null)
  const [isResolvingMyLocation, setIsResolvingMyLocation] = useState(false)
  const { isLoaded: mapsLoaded, loadError: mapsLoadError } = useGoogleMaps()
  const [quoteData, setQuoteData] = useState<QuoteData>({
    name: "",
    city: "",
    receiveFrom: "",
    receiveTo: "",
    currency: "ARS",
    description: "",
    jurisdiccion: "",
    zone: "",
    delivery: false,
  })

  const navigate = useNavigate()

  useEffect(() => {
    if (suggestedName && !hasInitializedName && !quoteData.name) {
      setQuoteData((prev) => ({ ...prev, name: suggestedName }))
      setHasInitializedName(true)
    }
  }, [suggestedName, hasInitializedName, quoteData.name])

  const currencyOptions = useMemo(
    () => [
      { value: "ARS", label: "Pesos argentinos" },
      { value: "USD", label: "Dólares estadounidenses" },
    ],
    [],
  )

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target
    setQuoteData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleCurrencyChange = (value: string) => {
    setQuoteData((prev) => ({
      ...prev,
      currency: value,
    }))
  }

  const handleLocationChange = (
    address: string,
    jurisdictionId: number | null,
    coords?: { lat: number; lng: number } | null
  ) => {
    setQuoteData((prev) => ({
      ...prev,
      city: address,
      jurisdiccion: jurisdictionId !== null ? jurisdictionId.toString() : prev.jurisdiccion,
      latitude: coords?.lat,
      longitude: coords?.lng,
    }))
  }

  const handleJurisdictionChange = (value: string) => {
    setQuoteData((prev) => ({
      ...prev,
      jurisdiccion: value,
    }))
  }

  const handleUseMyLocation = () => {
    if (mapsLoadError || !mapsLoaded) {
      setGeolocationMessage("El mapa aún no está listo. Esperá un momento o escribí la dirección.")
      return
    }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeolocationMessage("Tu navegador no permite acceder a la ubicación.")
      return
    }
    setGeolocationMessage(null)
    setIsResolvingMyLocation(true)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords
        try {
          const { formattedAddress, addressComponents } = await reverseGeocodeLatLng(latitude, longitude)
          const jurisdictionId = mapGoogleProvinceToJurisdiction(addressComponents, JURISDICTIONS)
          const display = sanitizeFormattedAddressForDisplay(formattedAddress)
          setQuoteData((prev) => ({
            ...prev,
            city: display,
            jurisdiccion: jurisdictionId !== null ? String(jurisdictionId) : "",
            latitude,
            longitude,
          }))
          setGeolocationMessage(
            jurisdictionId === null
              ? "No pudimos detectar la provincia automáticamente. Seleccioná provincia abajo si aparece el selector."
              : null
          )
        } catch {
          setGeolocationMessage(
            "No pudimos obtener la dirección a partir del punto. Probá escribiendo la ubicación o movete un poco e intentá de nuevo."
          )
        } finally {
          setIsResolvingMyLocation(false)
        }
      },
      (err) => {
        setIsResolvingMyLocation(false)
        if (err.code === 1) {
          setGeolocationMessage(
            "Permiso de ubicación denegado. Podés habilitarlo en la configuración del navegador o escribir la dirección."
          )
        } else {
          setGeolocationMessage("No pudimos obtener tu ubicación. Probá de nuevo o escribí la dirección.")
        }
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 }
    )
  }

  const convertDateToISOFormat = (date: string) => {
    if (!date) {
      return ""
    }
    const [dd, mm, yyyy] = date.split("/")
    return `${yyyy}-${mm}-${dd}`
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setShowPreview(false)

    if (!Array.isArray(materials) || materials.length === 0) {
      return
    }

    try {
      const payload = {
        ...quoteData,
        jurisdiccion: quoteData.jurisdiccion ? Number(quoteData.jurisdiccion) : null,
        delivery: quoteData.delivery === true,
      }

      if (autofillDraftId && String(autofillDraftId).trim()) {
        await publishFromAutofill(autofillDraftId, payload, materials, categoryRowsForAutofill, navigate)
      } else {
        await sendQuote(payload, materials, navigate)
      }
      fireAnalytics("send_quotation_finish")
      onSuccess()
      onClose()
    } catch (error) {
      console.error("Error al enviar la cotización:", error)
    }
  }

  useEffect(() => {
    const isValid =
      quoteData.name &&
      quoteData.city &&
      quoteData.jurisdiccion &&
      quoteData.receiveFrom &&
      quoteData.receiveTo &&
      quoteData.description

    setIsButtonDisabled(!isValid || !Array.isArray(materials) || materials.length === 0)
  }, [quoteData, materials])

  return (
    <ModalOverlay isOpen onClose={onClose} preventClose zIndex={32}>
      <motion.div variants={variants} initial="hidden" animate="visible" className={styles.popup}>
        <div className={styles.popup_header}>
          <div>
            <h2 className={styles.popup_header_title}>Enviar para cotizar</h2>
            <p className={styles.popup_header_subtitle}>
              Completá estos datos para que proveedores te envíen presupuestos
            </p>
          </div>
          <button type="button" onClick={onClose} className={styles.popup_header_cancelBtn}>
            Cancelar
          </button>
        </div>
        <form className={styles.popup_form} onSubmit={handleSubmit}>
          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Identificación</h3>
            <div className={styles.formGroup}>
              <label htmlFor="name">Nombre de la cotización *</label>
              <input
                id="name"
                name="name"
                type="text"
                value={quoteData.name}
                onChange={handleInputChange}
                placeholder="Ej: Obra casa Martínez, Refacción baño"
                required
              />
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Ubicación</h3>
            <div className={styles.locationInputRow}>
              <div className={styles.locationInputRow_field}>
                <LocationWithJurisdiction
                  addressValue={quoteData.city}
                  addressOnChange={(value) =>
                    setQuoteData((prev) => ({
                      ...prev,
                      city: value,
                      latitude: undefined,
                      longitude: undefined,
                    }))
                  }
                  jurisdictionValue={quoteData.jurisdiccion}
                  jurisdictionOnChange={handleJurisdictionChange}
                  onPlaceSelect={handleLocationChange}
                  placeholder="Ej: Pilar, Palermo, Córdoba"
                  jurisdictionPlaceholder="Seleccionar provincia"
                  hidePlacesLabel
                  placesInputAriaLabel="Ciudad, barrio o dirección de la obra"
                />
              </div>
              <button
                type="button"
                className={styles.locationGeoBtn}
                onClick={handleUseMyLocation}
                disabled={
                  isResolvingMyLocation || !!mapsLoadError || !mapsLoaded || typeof navigator === "undefined"
                }
                aria-label={isResolvingMyLocation ? "Obteniendo ubicación" : "Usar mi ubicación actual"}
                aria-busy={isResolvingMyLocation}
                title="Usar mi ubicación actual"
              >
                {isResolvingMyLocation ? (
                  <Loader2 size={20} className={styles.locationGeoBtn_spinner} aria-hidden />
                ) : (
                  <Crosshair size={20} strokeWidth={2} aria-hidden />
                )}
              </button>
            </div>
            {geolocationMessage ? <p className={styles.useMyLocationHint}>{geolocationMessage}</p> : null}
            <div className={styles.formCheckboxRow}>
              <input
                id="deliveryForMaterials"
                type="checkbox"
                checked={quoteData.delivery}
                onChange={(event) =>
                  setQuoteData((prev) => ({ ...prev, delivery: event.target.checked }))
                }
              />
              <label htmlFor="deliveryForMaterials">
                El pedido requiere entrega en la ubicación indicada.
              </label>
            </div>
            {quoteData.delivery &&
              (quoteData.latitude == null ||
                quoteData.longitude == null ||
                !Number.isFinite(quoteData.latitude) ||
                !Number.isFinite(quoteData.longitude)) && (
                <p className={styles.formHelpText}>
                  Si no elegís una ubicación del listado del mapa, las notificaciones a comercios usarán alcance nacional
                  para ese criterio.
                </p>
              )}
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Plazo</h3>
            <p className={styles.formHelpText}>
              Definí el período en que querés recibir ofertas. Después de esa fecha dejarán de llegar presupuestos.
            </p>
            <div className={styles.formGroup_inline}>
              <div className={styles.datePickerWrapper}>
                <label htmlFor="receiveFrom">Recibir desde *</label>
                <input
                  className={styles.datePicker}
                  id="receiveFrom"
                  name="receiveFrom"
                  type="date"
                  value={quoteData.receiveFrom}
                  onChange={(event) => {
                    handleInputChange(event)
                    if (quoteData.receiveTo && new Date(event.target.value) > new Date(quoteData.receiveTo)) {
                      setQuoteData((prev) => ({ ...prev, receiveTo: "" }))
                    }
                  }}
                  max={quoteData.receiveTo || undefined}
                  min={convertDateToISOFormat(todayDate)}
                  required
                />
              </div>
              <div className={styles.datePickerWrapper}>
                <label htmlFor="receiveTo">Hasta *</label>
                <input
                  className={styles.datePicker}
                  id="receiveTo"
                  name="receiveTo"
                  type="date"
                  value={quoteData.receiveTo}
                  onChange={(event) => {
                    handleInputChange(event)
                    if (quoteData.receiveFrom && new Date(event.target.value) < new Date(quoteData.receiveFrom)) {
                      setQuoteData((prev) => ({ ...prev, receiveFrom: "" }))
                    }
                  }}
                  min={quoteData.receiveFrom || convertDateToISOFormat(todayDate)}
                  required
                />
              </div>
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Condiciones</h3>
            <div className={styles.formGroup}>
              <label htmlFor="currency">Moneda *</label>
              <CustomSelect value={quoteData.currency} onChange={handleCurrencyChange} options={currencyOptions} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="description">Instrucciones o aclaraciones para el proveedor *</label>
              <textarea
                id="description"
                name="description"
                rows={3}
                value={quoteData.description}
                onChange={handleInputChange}
                placeholder="Ej: Incluir flete, entrega a domicilio, preferencia por materiales específicos"
              />
              <p className={styles.formHelpText}>
                Usá este espacio para indicar condiciones como flete incluido, entrega, forma de pago u otras preferencias.
              </p>
            </div>
          </div>

          <div className={styles.buttonGroup}>
            <button disabled={isButtonDisabled} type="submit" className={styles.sendBtn}>
              <Send size={16} />
              Publicar en edify
            </button>
          </div>
        </form>
      </motion.div>
    </ModalOverlay>
  )
}

const mapStateToProps = (state: any) => ({
  success: state.category.success,
  error: state.category.error,
  loading: state.category.loading,
})

export default connect(mapStateToProps, { sendQuote, publishQuoteFromAutofillDraft, resetSuccess })(SendQuotePopup)
